import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import Loading from "./Loading";

const retryRequest = async (axiosCall, retries = 3, delay = 1000) => {
  let lastError;
  for (let i = 0; i < retries; i++) {
    try {
      return await axiosCall();
    } catch (error) {
      lastError = error;
      console.warn(`Retry ${i + 1} failed. Retrying in ${delay}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
};

const BrandSelect = ({ cb, queryObject, setQueryObject }) => {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [locale, setLocale] = useState("it");

  useEffect(() => {
    const _loc = window?.Shopify?.locale;
    if (_loc) setLocale(_loc);
  }, []);

  const retrieveData = async () => {
    setLoading(true);
    try {
      const response = await retryRequest(() => axios.get("https://gp.bkt.studio/entity"));
      setData(response.data);
    } catch (e) {
      console.log("Final error retrieving brand data", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    retrieveData();
  }, []);

  return (
    <select
      style={{ flex: 1, maxWidth: "100px" }}
      onChange={(e) => {
        cb(e.target.value);
        setQueryObject({ ...queryObject, casa: e.target.value });
      }}
    >
      <option selected disabled>
        {locale === "it" ? "Produttore" : "Brand"}
      </option>
      {[...new Set(data?.map((d) => d.casa))]?.sort((a, b) => a.localeCompare(b)).map((brand, id) => (
        <option key={"casa-" + id} value={brand}>
          {brand}
        </option>
      ))}
    </select>
  );
};

const ModelliSelect = ({ cb, dependency, queryObject, setQueryObject }) => {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [locale, setLocale] = useState("it");

  useEffect(() => {
    const _loc = window?.Shopify?.locale;
    if (_loc) setLocale(_loc);
  }, []);

  const retrieveData = async () => {
    setLoading(true);
    try {
      const response = await retryRequest(() =>
        axios.post("https://gp.bkt.studio/entity/find", { casa: queryObject.casa })
      );
      setData(response.data);
    } catch (e) {
      console.log("Final error retrieving model data", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (dependency) {
      cb(null);
      retrieveData();
    }
  }, [dependency]);

  if (loading) return <LoadingIndicator />;

  return (
    <select
      disabled={!dependency}
      style={{ flex: 1 }}
      onChange={(e) => {
        cb(e.target.value);
        setQueryObject({ ...queryObject, casa: dependency, modello: e.target.value });
      }}
    >
      <option selected disabled>
        {locale === "it" ? "Modello" : "Model"}
      </option>
      {[...new Set(data?.map((d) => d.modello))]?.sort((a, b) => a.localeCompare(b)).map((modello, id) => (
        <option key={"modello-" + id} value={modello}>
          {modello}
        </option>
      ))}
    </select>
  );
};

const PorteSelect = ({ cb, dependency, queryObject, setQueryObject }) => {
  const [data, setData] = useState();
  const [locale, setLocale] = useState("it");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const _loc = window?.Shopify?.locale;
    if (_loc) setLocale(_loc);
  }, []);

  const retrieveData = async () => {
    setLoading(true);
    try {
      const response = await retryRequest(() =>
        axios.post("https://gp.bkt.studio/entity/find", {
          casa: queryObject.casa,
          modello: queryObject.modello,
        })
      );
      setData(response.data);
    } catch (e) {
      console.log("Final error retrieving porte data", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (dependency) {
      cb(null);
      retrieveData();
    }
  }, [dependency]);

  if (loading) return <LoadingIndicator />;

  return (
    <select
      disabled={!dependency}
      style={{ flex: 1 }}
      onChange={(e) => {
        cb(e.target.value);
        setQueryObject({ ...queryObject, porte: e.target.value });
      }}
    >
      <option selected disabled>
        {locale === "it" ? "Porte" : "Doors"}
      </option>
      {[...new Set(data?.map((d) => d.porte))]?.sort((a, b) => a.localeCompare(b)).map((porte, id) => (
        <option key={"porte-" + id} value={porte}>
          {porte}
        </option>
      ))}
    </select>
  );
};

const AnnoSelect = ({ cb, dependency, queryObject, setQueryObject }) => {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [locale, setLocale] = useState("it");

  useEffect(() => {
    const _loc = window?.Shopify?.locale;
    if (_loc) setLocale(_loc);
  }, []);

  const retrieveData = async () => {
    setLoading(true);
    try {
      const response = await retryRequest(() =>
        axios.post("https://gp.bkt.studio/entity/find", {
          casa: queryObject.casa,
          modello: queryObject.modello,
          porte: queryObject.porte,
        })
      );
      setData(response.data);
    } catch (e) {
      console.log("Final error retrieving anno data", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (dependency) {
      cb(null);
      retrieveData();
    }
  }, [dependency]);

  useEffect(() => {
    if (data && data.length === 1) {
      cb(data[0].metaId);
      setQueryObject({ ...queryObject, anno: data[0].name });
    }
  }, [data]);

  if (loading) return <LoadingIndicator />;

  return (
    <select
      disabled={!dependency}
      style={{ flex: 1 }}
      onChange={(e) => {
        cb(e.target.value);
        setQueryObject({ ...queryObject, anno: e.target.value });
      }}
    >
      <option selected disabled>
        {locale === "it" ? "Anno" : "Year"}
      </option>
      {[...new Set(data?.map((d) => d.anno))]?.sort((a, b) => a.localeCompare(b)).map((anno, id) => (
        <option key={"anno-" + id} value={anno}>
          {anno}
        </option>
      ))}
    </select>
  );
};

const LoadingIndicator = () => (
  <div
    style={{
      flex: 1,
      justifyContent: "center",
      display: "flex",
      alignItems: "center",
    }}
  >
    <Loading />
  </div>
);

export default function Advanced() {
  const { handleSubmit } = useForm();
  const [locale, setLocale] = useState("it");

  useEffect(() => {
    const _loc = window?.Shopify?.locale;
    if (_loc) setLocale(_loc);
  }, []);

  const [brand, setBrand] = useState();
  const [modelli, setModelli] = useState();
  const [porte, setPorte] = useState();
  const [anno, setAnno] = useState();
  const [queryObject, setQueryObject] = useState({});

  const generateHandle = (title) => {
    try {
      return title
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "") // rimuove caratteri non validi
  
    } catch (err) {
      return ""
    }
  }

  const onSubmit = async () => {
    try {
      const response = await retryRequest(() =>
        axios.post("https://gp.bkt.studio/entity/find", queryObject)
      );

      const result = response.data[0]?.slug?.replace("/", "").replace(".", "");




      if (result) {
        const basePath = locale === "it" ? "/collections/" : "/en/collections/";
        window.location.href = `${basePath}${generateHandle(result)}`;
      }
    } catch (e) {
      console.log("Final error in submit", e);
    }
  };

  return (
    <div>
      <form
        role="search"
        method="get"
        className="searchform"
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: "flex", flexDirection: "column" }}
      >
        <fieldset
          className="fs-advanced-search"
          style={{ display: "flex", justifyContent: "space-around" }}
        >
          <BrandSelect cb={setBrand} queryObject={queryObject} setQueryObject={setQueryObject} />
          <ModelliSelect
            dependency={brand}
            cb={setModelli}
            queryObject={queryObject}
            setQueryObject={setQueryObject}
          />
          <PorteSelect
            dependency={modelli}
            cb={setPorte}
            queryObject={queryObject}
            setQueryObject={setQueryObject}
          />
          <AnnoSelect
            dependency={porte}
            cb={setAnno}
            queryObject={queryObject}
            setQueryObject={setQueryObject}
          />
        </fieldset>
        <input
          type="submit"
          style={{ flex: 1 }}
          disabled={!anno}
          value={locale === "it" ? "Cerca" : "Search"}
        />
      </form>
    </div>
  );
}
